import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import { USE_CASES, getUseCase, setPageMeta } from "../data/useCases";
import { getSolutionPage } from "../data/solutionPages";
import { ArrowRight, ChevronRight, Gift, Play, XCircle, Info, Sparkles } from "lucide-react";

const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";

function BrowserFrame({ src, alt }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)] overflow-hidden">
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      </div>
      <img src={src} alt={alt} className="w-full block" loading="lazy" />
    </div>
  );
}

export default function UseCase() {
  const { slug } = useParams();
  const uc = getUseCase(slug);

  useEffect(() => {
    if (uc) setPageMeta(uc.seoTitle, uc.seoDescription);
    window.scrollTo(0, 0);
  }, [uc]);

  if (!uc) return <Navigate to="/cas-d-usage" replace />;

  const solution = getSolutionPage(uc.solutionSlug);
  const others = USE_CASES.filter((c) => c.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero immersif */}
      <section className="relative" data-testid="use-case-hero">
        <div className="max-w-7xl mx-auto px-3 sm:px-5 pt-4">
          <div className="relative overflow-hidden rounded-3xl">
            <img src={uc.heroImg} alt={uc.heroAlt} className="w-full h-[420px] md:h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-slate-950/10" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <nav className="flex items-center gap-1.5 text-xs text-white/60 mb-4" data-testid="use-case-breadcrumb">
                <Link to="/" className="hover:text-white">Accueil</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to="/cas-d-usage" className="hover:text-white">Cas d'usage</Link>
              </nav>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-300 mb-3">Cas d'usage</div>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight leading-tight text-white max-w-3xl" data-testid="use-case-title">
                {uc.title}
              </h1>
              <div className="mt-5 flex items-center gap-3">
                <span className="h-11 w-11 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center shrink-0">
                  <uc.profile.icon className="h-5 w-5 text-white" />
                </span>
                <div className="text-sm">
                  <div className="font-semibold text-white">{uc.profile.label}</div>
                  <div className="text-white/70">{uc.profile.detail}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corps : contenu + sidebar */}
      <section className="max-w-6xl mx-auto px-6 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-16">
            <p className="flex items-start gap-2 text-xs text-slate-400 -mb-8" data-testid="use-case-disclaimer-top">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Exemple illustratif — Les données, situations et bénéfices présentés constituent une
              simulation d'utilisation de Junago.
            </p>

            {/* Profil */}
            <div data-testid="use-case-context">
              <div className={`${OVERLINE} mb-3`}>Le profil</div>
              <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">{uc.profile.label}</h2>
              <div className="mt-5 space-y-4">
                {uc.context.map((p, i) => (
                  <p key={i} className="text-base text-slate-600 leading-relaxed">{p}</p>
                ))}
              </div>
            </div>

            {/* Problématique */}
            <div data-testid="use-case-challenges">
              <div className={`${OVERLINE} mb-3`}>La problématique</div>
              <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Les difficultés typiques de ce profil</h2>
              <div className="mt-6 space-y-3">
                {uc.challenges.map((c, i) => (
                  <div key={i} className="flex items-start gap-3.5 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid={`use-case-challenge-${i}`}>
                    <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700 leading-relaxed">{c}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Solution */}
            <div data-testid="use-case-solution">
              <div className={`${OVERLINE} mb-3`}>La solution Junago</div>
              <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Comment Junago répond à ce scénario</h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">{uc.solution.intro}</p>
              <div className="mt-6">
                <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 mb-3">Fonctionnalités mobilisées</div>
                <div className="flex flex-wrap gap-2">
                  {uc.solution.modules.map((m) => (
                    <Link
                      key={m.label}
                      to={m.to}
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50 transition-colors"
                      data-testid={`use-case-module-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {m.label} <ArrowRight className="h-3 w-3" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Capture */}
            {uc.capture && (
              <figure data-testid="use-case-capture">
                <BrowserFrame src={uc.capture.src} alt={uc.capture.caption} />
                <figcaption className="mt-4 text-sm text-slate-500 text-center">{uc.capture.caption}</figcaption>
              </figure>
            )}

            {/* Bénéfices potentiels */}
            <div data-testid="use-case-benefits">
              <div className={`${OVERLINE} mb-3`}>Bénéfices potentiels</div>
              <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Ce que ce scénario permet d'obtenir</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {uc.benefits.map((r) => (
                  <div key={r.title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`use-case-benefit-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <r.icon className="text-emerald-600" style={{ height: 18, width: 18 }} />
                    </div>
                    <h3 className="mt-3.5 font-heading text-base font-medium text-slate-900">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{r.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* À retenir */}
            <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 md:p-12" data-testid="use-case-takeaway">
              <div aria-hidden className="absolute -left-20 -top-32 h-72 w-72 rounded-full bg-violet-600 opacity-20 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-violet-300">
                  <Sparkles className="h-4 w-4" /> À retenir
                </div>
                <p className="mt-4 font-heading text-lg md:text-xl font-medium tracking-tight leading-snug max-w-2xl">
                  {uc.takeaway}
                </p>
              </div>
            </div>

            <p className="flex items-start gap-2 text-xs text-slate-400" data-testid="use-case-disclaimer">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Exemple illustratif — Les données, situations et bénéfices présentés constituent une
              simulation d'utilisation de Junago.
            </p>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="use-case-facts">
                <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 mb-4">En bref</div>
                <dl className="space-y-3.5">
                  {uc.facts.map((f) => (
                    <div key={f.label} className="flex items-start justify-between gap-4">
                      <dt className="text-sm text-slate-500">{f.label}</dt>
                      <dd className="text-sm font-semibold text-slate-800 text-right">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              {solution && (
                <Link
                  to={`/solutions/${solution.slug}`}
                  className="group block rounded-2xl border border-violet-200 bg-violet-50/60 p-6 hover:border-violet-300 hover:bg-violet-50 transition-colors"
                  data-testid="use-case-solution-link"
                >
                  <div className="text-xs font-bold tracking-[0.15em] uppercase text-violet-600 mb-2">Solution associée</div>
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 rounded-xl bg-white border border-violet-100 flex items-center justify-center shrink-0">
                      <solution.icon className="text-violet-600" style={{ height: 18, width: 18 }} />
                    </span>
                    <div className="font-heading text-base font-medium text-slate-900 group-hover:text-violet-700 transition-colors">{solution.menuLabel}</div>
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600">
                    Découvrir la solution <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              )}
              <div className="rounded-2xl bg-slate-950 text-white p-6" data-testid="use-case-sidebar-cta">
                <div className="font-heading text-lg font-medium tracking-tight">Essayez Junago gratuitement</div>
                <p className="mt-1.5 text-sm text-slate-400">30 jours d'essai complet, sans carte bancaire.</p>
                <div className="mt-4 space-y-2">
                  <Link to="/register?context=vendor" className="btn-jn-light w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="use-case-cta-start">
                    <Gift className="h-4 w-4" /> Commencer gratuitement
                  </Link>
                  <button type="button" onClick={openInteractiveDemo} className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors" data-testid="use-case-cta-demo">
                    <Play className="h-4 w-4" /> Démo interactive
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Autres cas d'usage */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="use-case-others">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className={`${OVERLINE} mb-6`}>Autres cas d'usage</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/cas-d-usage/${c.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-violet-300 hover:shadow-[0_12px_32px_-12px_rgba(124,58,237,0.25)] transition-all"
                data-testid={`use-case-other-${c.slug}`}
              >
                <div className="h-32 overflow-hidden">
                  <img src={c.heroImg} alt={c.heroAlt} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="font-heading text-sm font-medium text-slate-900 leading-snug group-hover:text-violet-700 transition-colors line-clamp-2">{c.title}</div>
                  <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600">
                    Découvrir le cas d'usage <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
